import React, { useEffect, useState, useCallback } from 'react';
import { BaseDraggable } from '../../../../mcmap-lib/src/components/Draggables/BaseDraggable';
import { useStore } from '../../../../mcmap-lib/src/stores/store';

import * as bufferOperator from "@arcgis/core/geometry/operators/bufferOperator";
import * as unionOperator from "@arcgis/core/geometry/operators/unionOperator";
import * as convexHullOperator from "@arcgis/core/geometry/operators/convexHullOperator";
import SketchViewModel from '@arcgis/core/widgets/Sketch/SketchViewModel';
import Graphic from '@arcgis/core/Graphic';
import { DraggableKeys } from '../../../../mcmap-lib/src/interfaces/config';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
// @ts-ignore
import PolygonIcon from "mcmap-lib/src/components/Draggables/Markup/icons/DrawPolygon.png";

const key = DraggableKeys.BUFFER_DRAGGABLE;

export enum BufferType {
	Municipal = 'municipal',
	Parcel = 'parcel'
}

export enum BufferMode {
	Features = 'features',
	Area = 'area'
}

export enum BufferUnit {
	Miles = 'miles',
	Feet = 'feet'
}

export enum BufferDistance {
	OneAndHalfMile = 1.5,
	ThreeMiles = 3
}

const PlanningBufferDraggable = () => {
	const {
		isActive,
		toggleActiveItem,
		results,
		addResults,
		setResults,
		activeResultsType,
		setActiveResultsType,
		primaryQueryLayer,
		highlightLayer,
		bufferLayer,
		bufferGraphic,
		setBufferGraphic,
		selectedResults,
		alternateQueryLayer,
		mapView
	} = useStore();

	// State for buffer type, mode, distance and units
	const [bufferType, setBufferType] = useState<BufferType>(BufferType.Municipal);
	const [bufferMode, setBufferMode] = useState<BufferMode>(BufferMode.Features);

	const [bufferUnits, setBufferUnits] = useState<BufferUnit>(BufferUnit.Miles);
	const [bufferDistance, setBufferDistance] = useState<BufferDistance>(BufferDistance.OneAndHalfMile);

	const [sketchViewModel, setSketchViewModel] = useState<SketchViewModel | null>(null);
	const [sketchLayer, setSketchLayer] = useState<GraphicsLayer | null>(null);

	const [isBufferAreaDrawing, setIsBufferAreaDrawing] = useState<boolean>(false);

	const [isLoading, setIsLoading] = useState<boolean>(false);

	const [bufferAreaGraphic, setBufferAreaGraphic] = useState<Graphic | null>(null);

	const MAX_BUFFER_DISTANCE_PARCEL_FEET = 26400; // 5 miles in feet

	const isAnySelected = results.length > 0;

	// Effect to reset distance and units when buffer type changes
	// useEffect(() => {
	//     //When buffer type changes reset the distance and units to defaults
	//     if (bufferType === BufferType.Municipal) {
	//         setBufferUnits(BufferUnit.Miles);
	//         setBufferDistance(BufferDistance.OneAndHalfMile);
	//     }
	// }, [bufferType]);

	// Effect to init the sketch view model when in area mode
	useEffect(() => {
		if (!mapView || bufferMode !== BufferMode.Area) {
			// clean up sketch
			if (sketchLayer) {
				mapView?.map?.remove(sketchLayer);
				setSketchLayer(null);
			}
			if (sketchViewModel) {
				sketchViewModel.destroy();
				setSketchViewModel(null);
			}
			return;
		}

		// Create sketch layer
		const areaSketchLayer = new GraphicsLayer({
			id: 'area-sketch-layer',
			listMode: 'hide',
			title: 'Area Sketch Layer'
		});

		mapView?.map?.add(areaSketchLayer, 0);
		setSketchLayer(areaSketchLayer);

		// Create SketchViewModel
		const svm = new SketchViewModel({
			layer: areaSketchLayer,
			view: mapView,
			updateOnGraphicClick: false,
			defaultCreateOptions: {
				mode: 'click'
			},
			polygonSymbol: {
				type: 'simple-fill',
				color: 'rgba(242, 189, 148, 0.5)',
				outline: {
					color: '#722620',
					width: 1
				}
			}
		});

		const svmHandle = svm.on('create', (event) => {
			const { state, graphic } = event;
			if (state === 'complete') {
				setIsBufferAreaDrawing(false);
				// areaSketchLayer.removeAll();
				graphic.symbol = {
					type: "simple-fill",
					color: [255, 100, 193, 0.5],
					style: "solid",
					outline: {
						color: [255, 100, 193],
						width: 4
					}
				};
				console.log('Area drawn graphic: ', graphic);
				mapView?.graphics.removeAll();
				mapView?.graphics.add(graphic);
				setBufferAreaGraphic(graphic);
			}
		});

		if (isBufferAreaDrawing) {
			svm.create('polygon');
		}

		setSketchViewModel(svm);

		// Cleanup on unmount
		return () => {
			mapView?.map?.remove(areaSketchLayer);
			svmHandle.remove();
			svm.destroy();
		}

	}, [mapView, bufferMode, bufferLayer, isBufferAreaDrawing]);


	// Function to handle buffer creation
	const onBufferClick = useCallback(async () => {
		if (!highlightLayer || !primaryQueryLayer) return;
		const geos = results.map((res) => res.geometry);
		const unionedGeo = unionOperator.executeMany(geos) as __esri.GeometryUnion;
		let combinedGeos = geos.length ? convexHullOperator.execute(unionedGeo) as __esri.GeometryUnion | null : null;

		if (bufferMode === BufferMode.Area && bufferAreaGraphic) {
			combinedGeos = bufferAreaGraphic.geometry as __esri.GeometryUnion;
		}
		console.log('Combined Geometries for Buffering: ', combinedGeos);
		setIsLoading(true);

		const bufferGeo = bufferOperator.execute(
			Array.isArray(combinedGeos) ? combinedGeos[0] : combinedGeos,
			bufferDistance,
			{ unit: bufferUnits as __esri.LengthUnit }
		) as __esri.GeometryUnion;

		const symbol = {
			type: 'simple-fill',
			color: [255, 100, 193, 0.7],
			outline: {
				color: [255, 100, 193],
				width: 2,
			},
		} as any;

		const bufferGraphic = new Graphic({
			geometry: bufferGeo as any,
			symbol,
		});
		mapView?.graphics.removeAll();
		bufferLayer?.removeAll();
		bufferLayer?.add(bufferGraphic);
		setBufferGraphic(bufferGraphic);

		// Maintain old behavior for parcel buffer
		if (bufferType === BufferType.Parcel) {
			const res = await primaryQueryLayer.queryFeatures({
				geometry: bufferGeo as any,
				returnGeometry: true,
				outFields: ["*"],
			});
			setActiveResultsType("parcel");
			setResults(res.features);
			setIsLoading(false);
			return;
		}
		// Behavior for municipal buffer
		else if (bufferType === BufferType.Municipal) {
			if (!alternateQueryLayer) return;
			setActiveResultsType(BufferType.Municipal);
			const response = await alternateQueryLayer.queryFeatures({
				geometry: bufferGeo as any,
				returnGeometry: true,
				outFields: ["*"],
			});
			if (response.features.length > 0) {
				const firstFeature = response.features[0];
				const unincorporatedGeo = firstFeature.clone();
				unincorporatedGeo.attributes.CORPNAME = "Unincorporated McHenry County";
				setResults([...response.features, unincorporatedGeo]);
			}
			else {
				setResults([]);
			}
			setIsLoading(false);
		}
	}, [
		highlightLayer,
		primaryQueryLayer,
		alternateQueryLayer,
		bufferDistance,
		bufferUnits,
		bufferType,
		selectedResults,
		results,
		bufferMode,
		bufferAreaGraphic
	]);

	const onClearBufferClick = () => {
		bufferLayer?.removeAll();
		mapView?.graphics.removeAll();
		setBufferGraphic(null);
	};

	useEffect(() => {
		bufferLayer?.removeAll();
		setBufferGraphic(null);
	}, [bufferType, bufferUnits, bufferDistance, bufferMode]);


	const bufferDistanceInFeet = bufferUnits === BufferUnit.Miles ? bufferDistance * 5280 : bufferDistance;
	const isBufferDistanceValid = bufferType === BufferType.Parcel ? bufferDistanceInFeet <= MAX_BUFFER_DISTANCE_PARCEL_FEET : true;

	const isBufferButtonEnabled = bufferMode === BufferMode.Features ?
		isAnySelected && isBufferDistanceValid && !isLoading :
		bufferAreaGraphic !== null && isBufferDistanceValid && !isLoading;

	return (
		<BaseDraggable
			id={key}
			onClose={() => toggleActiveItem(key)}
			visible={isActive(key)}
			initialPosition='top-right'
			className=''
			headerText='BUFFER'
			size='sm'
		>
			<>
				{/* Buffer Type Section */}
				<div className='mb-3'>
					<p className='font-semibold text-sm mb-1'> Buffer Type: </p>
					<div className='flex flex-col gap-1'>
						<label className='flex items-center gap-2 cursor-pointer'>
							<input
								disabled={isLoading}
								type='radio'
								name='bufferType'
								value={BufferType.Municipal}
								checked={bufferType === BufferType.Municipal}
								onChange={() => {
									setBufferType(BufferType.Municipal)
								}}
							/>
							<span> Municipal Limits Buffer </span>
						</label>

						<label className='flex items-center gap-2 cursor-pointer'>
							<input
								disabled={isLoading}
								type='radio'
								name='bufferType'
								value={BufferType.Parcel}
								checked={bufferType === BufferType.Parcel}
								onChange={() => {
									setBufferType(BufferType.Parcel)
								}}
							/>
							<span> Parcel Buffer </span>
						</label>
					</div>
				</div>

				<hr className='my-3 border-t border-gray-300' />

				{/* Features or Area Section */}
				<div className='mb-3'>
					<p className='font-semibold text-sm mb-1'> Buffer Selected Features or Area </p>
					<div className='flex flex-col gap-1'>
						<label className='flex items-center gap-2 cursor-pointer'>
							<input
								type='radio'
								name='bufferMode'
								value={BufferMode.Features}
								checked={bufferMode === BufferMode.Features}
								onChange={() => setBufferMode(BufferMode.Features)}
							/>
							<span> Features </span>
						</label>

						<label className='flex items-center gap-2 cursor-pointer'>
							<input
								type='radio'
								name='bufferMode'
								value={BufferMode.Area}
								checked={bufferMode === BufferMode.Area}
								onChange={() => setBufferMode(BufferMode.Area)}
							/>
							<span> Area </span>
						</label>
					</div>
				</div>

				<hr className='my-3 border-t border-gray-300' />

				{/* Distance Section */}
				<div className='mb-3'>
					<p className='font-semibold text-sm mb-1'> Distance: </p>
					<div className='flex flex-col gap-1'>
						{bufferType === BufferType.Municipal ? (
							<select
								disabled={isLoading}
								value={bufferDistance}
								onChange={(ev) => setBufferDistance(Number(ev.target.value) as BufferDistance)}
							>
								<option value={BufferDistance.OneAndHalfMile}> 1.5 Miles </option>
								<option value={BufferDistance.ThreeMiles}> 3 Miles </option>
							</select>
						) : (
							<div className='flex items-center my-2'>
								<input
									disabled={!isAnySelected || isLoading}
									value={bufferDistance}
									onChange={(e) => setBufferDistance(parseInt(e.target.value))}
									type='number'
									className='border border-gray-300 p-1 rounded-sm'
									min={1}
									max={1000}
								/>
								<select
									disabled={!isAnySelected || isLoading}
									value={bufferUnits}
									className='border border-gray-300 p-0.5 rounded-sm ml-2'
									onChange={(e) => setBufferUnits(e.target.value as BufferUnit)}
								>
									<option value='feet'>Feet</option>
									<option value='miles'>Miles</option>
								</select>
							</div>
						)}
					</div>
				</div>

				{/* Buffer Area Sketch Section */}
				{bufferMode === BufferMode.Area && (
					<div>
						<p>Buffer Area Tool</p>
						<button
							title={"Polygon"}
							className={`btn-athena w-9 h-9 p-1 m-1 ${isBufferAreaDrawing ? "active" : ""}`}
							key={"polygon"}
							onClick={() => setIsBufferAreaDrawing(prev => !prev)}
						>
							<img src={PolygonIcon} alt="Polygon" />
						</button>
					</div>
				)}

				{!isAnySelected && bufferMode !== BufferMode.Area && (
					<div className='bg-red-100 text-red-600 border border-red-500 p-1 rounded-sm my-4'>
						No feature selected, please select a feature to buffer.
					</div>
				)}

				{!isBufferDistanceValid && (
					<div className='bg-red-100 text-red-600 border border-red-500 p-1 rounded-sm my-4'>
						Buffer distance invalid. Please enter a value less than 5 miles (26400 feet).
					</div>
				)}
				<hr className='my-3 border-t border-gray-300' />
				<div className='flex justify-end gap-2 mt-3'>
					<button
						onClick={onBufferClick}
						disabled={!isBufferButtonEnabled}
						className={`btn-athena btn-xs mr-2 ${!isBufferButtonEnabled ? 'btn-disabled opacity-50 cursor-not-allowed' : ''}`}
					>
						{isLoading ? 'Buffering...' : 'Buffer'}
					</button>

					<button
						onClick={onClearBufferClick}
						disabled={!bufferGraphic || isLoading}
						className={`btn-athena btn-xs ${!bufferGraphic || isLoading ? 'btn-disabled opacity-50 cursor-not-allowed' : ''}`}
					>
						Clear Buffer
					</button>
				</div>
			</>
		</BaseDraggable >
	);
}

export { PlanningBufferDraggable };